import { EscriptStatusRecord, EscriptTimelineStep } from '../escript/pharmacy-escript.descriptor';

/** Vendor tag appended to the shared HBC order-number format (see buildEscriptOrderNumber). */
export const EASYRX_ORDER_SUFFIX = 'ER';

/**
 * EasyRx (Modifly "HealthBridge Rx Submission Integration") DTOs — verbatim from the live Swagger
 * (`api.easyrx-healthbridge.modifly.app/swagger/v1/swagger.json`). The Angular app never calls EasyRx
 * directly; it calls the Florence proxy (`api/v2/.../easyrx/*`) whose responses wrap the raw EasyRx
 * JSON as a string (`ScriptResponse` / `StatusResponse`) — parse that to get these shapes.
 *
 * Status is a free-form string + int code with NO enum defined yet (EasyRx to deliver an enum status
 * endpoint later). Do NOT hardcode CompuAssist's numeric codes — labels come from `status` directly and
 * pill/terminal state from the string heuristic below until the enum endpoint lands.
 */

/** `PharmacyDTO` / `PharmacyBlockDTO` — Dischem-shaped (favourite/identifier key = `siteNo`). */
export interface EasyRxPharmacy {
  siteNo?: string | null;
  practiceNo?: string | null;
  yNumbers?: string | null;
  storeName?: string | null;
  storeAddress?: string | null;
  town?: string | null;
  postalCode?: string | null;
  province?: string | null;
  lat?: string | null;
  lng?: string | null;
  tradingHours?: string | null;
  telephoneNo?: string | null;
  dispensaryNo?: string | null;
  email?: string | null;
  /** UI-only: overlaid from the favourites config, not part of the DTO. */
  isFavourite?: boolean;
}

export interface EasyRxAddressDTO {
  line1?: string;
  line2?: string;
  line3?: string;
  code?: string;
}

export interface EasyRxPrescriptionLineDTO {
  nappiCode?: string;
  nappiCode10?: string;
  medicationDescription?: string;
  quantity?: number;
  dosageType?: string;
  dosageUnits?: number;
  durationType?: string;
  durationUnit?: number;
  periodType?: string;
  periodUnit?: number;
  frequencyUnits?: number;
  repeat?: number;
  chronicIndicator?: boolean;
  allowGeneric?: boolean;
  diagnosis?: Array<{ diagnosisCode?: string; diagnosisDescription?: string }>;
  parameters?: { [key: string]: string };
  lineNum?: number;
}

export interface EasyRxMedicationPrescriptionDTO {
  medicationPrescriptionId?: string;
  issuedDate?: string;
  name?: string;
  orderNumber?: string;
  provider?: {
    name?: string;
    practiceNumber?: string;
    emailAddress?: string;
    cellphoneNumber?: string;
    physicalAddress?: string;
    postalAddress?: string;
  };
  patientDetails?: {
    title?: string;
    firstName?: string;
    surname?: string;
    dateOfBirth?: string;
    identityNo?: string;
    passportNo?: string;
    country?: string;
    gender?: string;
    contactNo?: string;
    emailAddress?: string;
    physicalAddress?: EasyRxAddressDTO;
    postalAddress?: EasyRxAddressDTO;
  };
  mainMemberDetails?: {
    title?: string;
    firstName?: string;
    surname?: string;
    dateOfBirth?: string;
    identityNo?: string;
    passportNo?: string;
    country?: string;
    gender?: string;
    contactNo?: string;
    emailAddress?: string;
  };
  medicationPrescriptionLines?: EasyRxPrescriptionLineDTO[];
}

export interface SendEasyRxPrescriptionRequestDTO {
  prescription: EasyRxMedicationPrescriptionDTO;
  scriptPdfBase64: string;
  pharmacy: EasyRxPharmacy;
}

export interface SendEasyRxPrescriptionResponseDTO {
  success?: boolean;
  orderNumber?: string;
  messageId?: string;
  timestamp?: string;
  status?: string;
  message?: string;
}

/** `GET /pharmacies` page (limit/offset) — the admin Refresh pages through this to rebuild the catalogue. */
export interface EasyRxPharmacyListResponseDTO {
  success?: boolean;
  totalCount?: number;
  pharmacies?: EasyRxPharmacy[] | null;
}

export interface EasyRxPrescriptionStatusResponseDTO {
  success?: boolean;
  orderNumber?: string;
  status?: string; // free-form label, no enum defined yet
  statusCode?: number;
  message?: string;
  timestamp?: string;
  estimatedReadyTime?: string;
  pharmacyNotes?: string;
  totalAmount?: number | null;
  medicalAidCovered?: number | null;
  amountDue?: number | null;
}

/**
 * Friendly, actor-explicit labels for the KNOWN EasyRx status strings ("received by whom?" was
 * ambiguous). Unknown strings pass through raw until EasyRx ships its status enum endpoint.
 */
const EASYRX_STATUS_LABELS: { [status: string]: { label: string; description: string } } = {
  received: { label: 'Received by pharmacy', description: 'The pharmacy has received the prescription.' },
};

export function easyRxStatusLabel(status: string): string {
  return EASYRX_STATUS_LABELS[status.trim().toLowerCase()]?.label ?? status;
}

function easyRxStatusDescription(status: string): string | undefined {
  return EASYRX_STATUS_LABELS[status.trim().toLowerCase()]?.description;
}

/** Heuristic pill colour from the free-form status string (until the enum endpoint lands). */
export function easyRxPillColor(status: string | undefined | null): 'orange' | 'green' | 'red' {
  const s = (status ?? '').toLowerCase();
  if (/reject|cancel|fail|declin|error/.test(s)) {
    return 'red';
  }
  if (/ready|collect|dispens|complete|fulfil|approved/.test(s)) {
    return 'green';
  }
  return 'orange';
}

/**
 * Maps the parsed EasyRx status DTO onto the unified status record. Money fields are formatted to
 * strings here so the generic modal's financial section renders `R {value}` consistently.
 */
export function toEasyRxStatusRecord(dto: EasyRxPrescriptionStatusResponseDTO | null): EscriptStatusRecord | null {
  if (!dto) {
    return null;
  }
  const money = (v: number | null | undefined): string | undefined => (v === null || v === undefined ? undefined : v.toFixed(2));
  return {
    statusCode: dto.statusCode ?? -1,
    statusUpdatedAt: dto.timestamp ?? new Date().toISOString(),
    statusHistory: [],
    status: dto.status ?? '',
    totalAmount: money(dto.totalAmount),
    amountDue: money(dto.amountDue),
    medicalAidCovered: money(dto.medicalAidCovered),
    estimatedReadyTime: dto.estimatedReadyTime,
    pharmacyNotes: dto.pharmacyNotes,
    custommessage: dto.message,
  };
}

/**
 * Two-step timeline: "Submitted" (always done) + the current pharmacy status, whose state follows the
 * string heuristic. Once the EasyRx enum endpoint exists, drive the intermediate steps from it.
 */
export function buildEasyRxTimelineSteps(record: EscriptStatusRecord): EscriptTimelineStep[] {
  const status = (record.status as string) || '';
  const color = easyRxPillColor(status);
  const currentState: EscriptTimelineStep['state'] = color === 'green' ? 'completed' : color === 'red' ? 'rejected' : 'active';
  const steps: EscriptTimelineStep[] = [
    {
      statusCode: 0,
      label: 'Sent to pharmacy',
      icon: 'send',
      description: 'Prescription submitted to EasyRx.',
      state: 'completed',
    },
  ];
  if (status) {
    steps.push({
      statusCode: record.statusCode,
      label: easyRxStatusLabel(status),
      icon: color === 'green' ? 'check_circle' : color === 'red' ? 'cancel' : 'hourglass_top',
      description:
        easyRxStatusDescription(status) ||
        (record.custommessage as string) ||
        (record.pharmacyNotes as string) ||
        'Latest status from the pharmacy.',
      state: currentState,
      timestamp: record.statusUpdatedAt,
    });
  }
  return steps;
}

// ─── Florence proxy envelopes (api/v2 .../easyrx/*) ─────────────────────────────────────────────
// Hand-maintained here because api-client.service.ts is NSwag-generated (no hand-added types).
// The proxy wraps the raw EasyRx JSON as a string; parse the inner payload into the DTOs above.

export interface SendEasyRxScript {
  ScriptRequest?: string | null;
}

export interface RestApiResultOfSendEasyRxScriptResponse {
  Sucess?: boolean | null;
  ResponseCode?: number | null;
  ResponseMessage?: string | null;
  Data?: SendEasyRxScriptResponse | null;
  Links?: any[] | null;
}

export interface SendEasyRxScriptResponse {
  ScriptResponse?: string | null;
}

export interface RestApiResultOfEasyRxPrescriptionStatusResponse {
  Sucess?: boolean | null;
  ResponseCode?: number | null;
  ResponseMessage?: string | null;
  Data?: EasyRxPrescriptionStatusResponse | null;
  Links?: any[] | null;
}

export interface EasyRxPrescriptionStatusResponse {
  StatusResponse?: string | null;
}

export interface RestApiResultOfEasyRxPharmaciesResponse {
  Sucess?: boolean | null;
  ResponseCode?: number | null;
  ResponseMessage?: string | null;
  Data?: EasyRxPharmaciesResponse | null;
  Links?: any[] | null;
}

export interface EasyRxPharmaciesResponse {
  PharmaciesResponse?: string | null;
}
